import {
  Action,
  AionzSnackbar,
  AionzTooltip,
  ConfirmResult,
  ConfirmationModalService,
  LoadingService,
  ModalTypes,
  PluralizadorPipe
} from '@aionzdev/design-system';
import { DatePipe, isPlatformBrowser } from '@angular/common';
import { HasUnconfirmedChanges } from '../../../guards/unconfirmed-review.guard';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  PLATFORM_ID,
  computed,
  inject,
  signal,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, auditTime, combineLatest, distinctUntilChanged, finalize, map, takeUntil, tap, of, Observable, filter } from 'rxjs';
import { WebsocketService } from '@aionzdev/modules';
import { LoadState, PurchaseRequestDetailTab, PurchaseRequestStatus } from '../../../enums';
import { PurchaseItemStatus } from '../../../interfaces/purchase-request';
import { PurchaseRequestResponseDto } from '../../../interfaces/purchase-response.dto';
import { PurchasingEvent } from '../../../interfaces/purchasing-event';
import { SendPurchaseRequestForQuoteItem } from '../../../interfaces/send-purchase-request-for-quote-dialog-data';
import { PurchaseRequestItemTableAction, PurchaseRequestItemTableActionEvent } from '../../../interfaces/purchase-request-item-table-action';
import { UpdatePurchaseOrderItemDto } from '../../../interfaces/update-purchase-order-status.dto';
import { AbstractPurchasingService } from '../../../services/abstract-purchasing.service';
import { PurchaseRequestStatusComponent } from '../../atoms/purchase-request-status/purchase-request-status.component';
import { PurchaseRequestAttachmentsComponent } from '../../components/purchase-request-attachments/purchase-request-attachments.component';
import { PurchaseRequestActivityPanelComponent } from '../../components/purchase-request-activity-panel/purchase-request-activity-panel.component';
import { PurchaseRequestItemsTableComponent } from '../../components/purchase-request-items-table/purchase-request-items-table.component';
import { PurchaseBatchActionBarComponent } from '../../components/purchase-batch-action-bar/purchase-batch-action-bar.component';
import { BranchesTooltipComponent } from '../../atoms/branches-tooltip/branches-tooltip.component';
import {
  deliveryBranchesLabel,
  injectBranchCities,
  toDeliveryBranchesSummary
} from '../../utils/branch-label.util';
import {
  PurchaseItemStatusFilterComponent,
  StatusFilterOption
} from '../../components/purchase-item-status-filter/purchase-item-status-filter.component';

@Component({
  selector: 'aionz-pending-purchase-request-detail',
  imports: [
    DatePipe,
    AionzTooltip,
    MatButtonModule,
    MatChipsModule,
    MatIconModule,
    MatTabsModule,
    PurchaseRequestStatusComponent,
    PurchaseRequestAttachmentsComponent,
    PurchaseRequestActivityPanelComponent,
    PurchaseRequestItemsTableComponent,
    PurchaseBatchActionBarComponent,
    PurchaseItemStatusFilterComponent
  ],
  templateUrl: './pending-purchase-request-detail.component.html',
  styleUrl: './pending-purchase-request-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '(window:beforeunload)': 'onBeforeUnload($event)' }
})
export class PendingPurchaseRequestDetailComponent implements HasUnconfirmedChanges, OnInit {
  private readonly confirmationModal = inject(ConfirmationModalService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly loadingService = inject(LoadingService);
  private readonly purchasingService = inject(AbstractPurchasingService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly snackBar = inject(AionzSnackbar);
  private readonly pluralizador = new PluralizadorPipe();
  private readonly returnUrl = this.router.currentNavigation()?.extras.state?.['returnUrl'] as
    | string
    | undefined;

  private readonly websocketService = inject(WebsocketService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly websocketUpdate$ = new Subject<void>();
  private detailRealtimeDestroy$ = new Subject<void>();

  protected readonly activityPanel = viewChild.required(PurchaseRequestActivityPanelComponent);

  protected readonly purchase = signal<PurchaseRequestResponseDto | null>(null);
  protected readonly items = signal<SendPurchaseRequestForQuoteItem[]>([]);

  protected readonly branchesTooltipComponent = BranchesTooltipComponent;
  protected readonly branchCities = injectBranchCities();
  /** Locais de entrega dos itens, para o chip do topo (nome e endereço na dica). */
  protected readonly deliveryBranchesSummary = computed(() =>
    toDeliveryBranchesSummary(this.items(), this.branchCities())
  );
  protected readonly deliveryBranchesLabel = computed(() =>
    deliveryBranchesLabel(this.deliveryBranchesSummary())
  );
  protected readonly itemStatuses = signal<Record<string, PurchaseItemStatus>>({});
  protected readonly declineReasons = signal<Record<string, string>>({});
  protected readonly selectedTabIndex = signal(PurchaseRequestDetailTab.DETAILS);
  protected readonly detailsLoadState = signal<LoadState>(LoadState.IDLE);
  protected readonly selectedItemIds = signal<Set<string>>(new Set());
  protected readonly statusFilter = signal<StatusFilterOption>('ALL');

  protected readonly LoadState = LoadState;
  protected readonly PurchaseRequestDetailTab = PurchaseRequestDetailTab;
  protected readonly PurchaseRequestStatus = PurchaseRequestStatus;

  private readonly _defaultReason = 'Recusou sem motivos';

  protected currentPurchaseId = '';

  constructor() {
    this.destroyRef.onDestroy(() => {
      if (isPlatformBrowser(this.platformId)) {
        const config = this.purchasingService.getRealtimeConfig();
        this.websocketService.sendMessage(config.leaveEvent, config.tenantId);
      }
      this.detailRealtimeDestroy$.next();
      this.detailRealtimeDestroy$.complete();
    });
  }

  protected readonly itemsWithStatus = computed(() => {
    const rawItems = this.items();
    const statuses = this.itemStatuses();
    return rawItems.map(item => ({
      ...item,
      status: statuses[item.orderId] || PurchaseItemStatus.PENDING
    }));
  });

  protected readonly filteredItemsWithStatus = computed(() => {
    const filter = this.statusFilter();
    const items = this.itemsWithStatus();

    if (filter === 'ALL') {
      return items;
    }

    return items.filter(item => item.status === filter);
  });

  protected readonly canConfirm = computed(() => {
    const items = this.itemsWithStatus();
    return items.length > 0 && items.every(item => item.status !== PurchaseItemStatus.PENDING);
  });

  /** Itens já aprovados ou recusados, para o contador ao lado de "Confirmar". */
  /** Contagem por status para os chips de filtro. */
  protected readonly statusCounts = computed(() => {
    const items = this.itemsWithStatus();
    const count = (status: PurchaseItemStatus) => items.filter(item => item.status === status).length;
    return {
      ALL: items.length,
      [PurchaseItemStatus.PENDING]: count(PurchaseItemStatus.PENDING),
      [PurchaseItemStatus.APPROVED]: count(PurchaseItemStatus.APPROVED),
      [PurchaseItemStatus.DECLINED]: count(PurchaseItemStatus.DECLINED)
    };
  });

  protected readonly reviewedCount = computed(
    () => this.itemsWithStatus().filter(item => item.status !== PurchaseItemStatus.PENDING).length
  );

  protected readonly approvedCount = computed(
    () => this.itemsWithStatus().filter(item => item.status === PurchaseItemStatus.APPROVED).length
  );

  protected readonly declinedCount = computed(
    () => this.itemsWithStatus().filter(item => item.status === PurchaseItemStatus.DECLINED).length
  );

  /** Liberado depois de confirmar com sucesso: a navegação de volta não deve perguntar. */
  private readonly leavingAfterConfirm = signal(false);

  /** Itens aprovados ou recusados nesta tela que ainda não foram enviados. */
  protected readonly unconfirmedCount = computed(() =>
    this.leavingAfterConfirm() ? 0 : this.reviewedCount()
  );

  /** Guard de saída: pergunta se há revisão não confirmada. */
  public canLeave(): boolean | Observable<boolean> {
    const count = this.unconfirmedCount();
    if (count === 0) return true;

    const itens = count === 1 ? '1 item revisado' : `${count} itens revisados`;

    // Enquanto o guard espera a resposta, a navegação fica "em andamento" e o design-system liga o
    // loading global (NavigationLoadingService, 200ms após o início). Mantém desligado com o dialog aberto.
    const keepLoadingOff = this.loadingService.isLoading$
      .pipe(filter(Boolean))
      .subscribe(() => this.loadingService.hideLoading());

    return this.confirmationModal
      .open(
        {
          headerTitle: 'Revisão não confirmada',
          title: 'Sair sem confirmar?',
          description: `Você tem ${itens} sem confirmar. Se sair agora, as aprovações e recusas desta tela serão perdidas.`,
          labelConfirm: 'Continuar revisando',
          labelCancel: 'Sair e perder alterações'
        },
        ModalTypes.WARNING
      )
      .pipe(
        map(result => result === Action.CANCEL),
        finalize(() => keepLoadingOff.unsubscribe())
      );
  }

  /** Fechar ou recarregar a aba: aviso nativo do navegador (o texto é do próprio navegador). */
  protected onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.unconfirmedCount() > 0) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  /** Por que "Confirmar" está desabilitado (null quando está liberado). */
  protected readonly confirmBlockedReason = computed(() => {
    const total = this.itemsWithStatus().length;
    if (total === 0) return 'Não há itens nesta solicitação.';
    const pending = total - this.reviewedCount();
    if (pending === 0) return null;
    return pending === 1
      ? 'Aprove ou recuse o item pendente para confirmar.'
      : `Aprove ou recuse os ${pending} itens pendentes para confirmar.`;
  });

  protected readonly tableActions = signal<PurchaseRequestItemTableAction[]>([
    {
      type: 'approve-item',
      label: 'Aprovar item',
      shortLabel: 'Aprovar',
      icon: 'check',
      inline: true,
      tone: 'positive',
      visible: (item: SendPurchaseRequestForQuoteItem) =>
        this.itemStatuses()[item.orderId] !== PurchaseItemStatus.APPROVED
    },
    {
      type: 'decline-item',
      label: 'Recusar item',
      shortLabel: 'Recusar',
      icon: 'close',
      inline: true,
      tone: 'negative',
      visible: (item: SendPurchaseRequestForQuoteItem) =>
        this.itemStatuses()[item.orderId] !== PurchaseItemStatus.DECLINED
    },
    {
      type: 'change-reason',
      label: 'Alterar motivo da recusa',
      icon: 'edit',
      visible: (item: SendPurchaseRequestForQuoteItem) =>
        this.itemStatuses()[item.orderId] === PurchaseItemStatus.DECLINED
    }
  ]);

  ngOnInit(): void {
    combineLatest([
      this.route.paramMap.pipe(
        map(params => params.get('purchaseId') || ''),
        distinctUntilChanged()
      ),
      this.route.queryParamMap.pipe(
        map(params => params.get('tab')),
        distinctUntilChanged()
      )
    ])
      .pipe(
        tap(([purchaseId, tab]) => {
          const tabIndex =
            tab === 'anexos'
              ? PurchaseRequestDetailTab.ATTACHMENTS
              : PurchaseRequestDetailTab.DETAILS;
          const isNewPurchase = purchaseId !== this.currentPurchaseId;

          if (isNewPurchase) {
            this.currentPurchaseId = purchaseId;
            this.loadDetail(purchaseId);

            if (isPlatformBrowser(this.platformId)) {
              this.setupRealtimeUpdates();
            }
          }

          this.selectedTabIndex.set(tabIndex);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private setupRealtimeUpdates(): void {
    this.detailRealtimeDestroy$.next();

    if (!this.currentPurchaseId) return;

    const config = this.purchasingService.getRealtimeConfig();
    const updateTypes = new Set(config.updateTypes.pendingDetail || []);

    this.websocketUpdate$
      .pipe(auditTime(500), takeUntil(this.detailRealtimeDestroy$), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadDetail(this.currentPurchaseId);
      });

    this.websocketService.sendMessage(config.joinEvent, config.tenantId);

    this.websocketService.listen(config.eventName, (event: any) => {
      const mappedEvent: PurchasingEvent = this.purchasingService.getPurchasingEvent(event);
      if (
        String(mappedEvent.tenantId) === String(config.tenantId) &&
        mappedEvent.purchaseId === this.currentPurchaseId &&
        updateTypes.has(mappedEvent.type)
      ) {
        this.websocketUpdate$.next();
      }
    });
  }

  protected handleSelectAllToggle(currentlyAllSelected: boolean): void {
    if (currentlyAllSelected) {
      const visibleOrderIds = new Set(this.filteredItemsWithStatus().map(i => i.orderId));
      this.selectedItemIds.update(set => {
        const next = new Set(set);
        visibleOrderIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      const visibleOrderIds = this.filteredItemsWithStatus().map(i => i.orderId);
      this.selectedItemIds.update(set => {
        const next = new Set(set);
        visibleOrderIds.forEach(id => next.add(id));
        return next;
      });
    }
  }

  protected handleRowSelectToggle(orderId: string): void {
    this.selectedItemIds.update(set => {
      const next = new Set(set);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  }

  protected clearSelection(): void {
    this.selectedItemIds.set(new Set());
  }

  protected batchApprove(): void {
    const selectedCount = this.selectedItemIds().size;
    if (selectedCount === 0) return;

    const itemNoun = this.pluralizador.transform('item', selectedCount);
    const approvedNoun = this.pluralizador.transform('aprovado', selectedCount);

    this.confirmationModal
      .open(
        {
          headerTitle: 'Confirmar Aprovação em Lote',
          title: `Aprovar ${selectedCount} ${itemNoun}?`,
          description: `Você tem certeza que deseja aprovar ${selectedCount === 1 ? 'o item selecionado' : `os ${selectedCount} itens selecionados`}?`,
          labelConfirm: 'Aprovar',
          labelCancel: 'Cancelar'
        },
        ModalTypes.INFO
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (result === Action.CONFIRM) {
          const selectedIds = this.selectedItemIds();
          this.itemStatuses.update(statuses => {
            const next = { ...statuses };
            selectedIds.forEach(id => {
              next[id] = PurchaseItemStatus.APPROVED;
            });
            return next;
          });
          this.declineReasons.update(reasons => {
            const next = { ...reasons };
            selectedIds.forEach(id => delete next[id]);
            return next;
          });
          this.clearSelection();
          this.snackBar.success(
            `${selectedCount} ${itemNoun} ${approvedNoun}. Não esqueça de confirmar o envio.`
          );
        }
      });
  }

  protected batchDecline(): void {
    const selectedCount = this.selectedItemIds().size;
    if (selectedCount === 0) return;

    const itemNoun = this.pluralizador.transform('item', selectedCount);
    const declinedNoun = this.pluralizador.transform('recusado', selectedCount);

    this.confirmationModal
      .openWithJustification(
        {
          headerTitle: 'Recusar itens selecionados',
          title: 'Motivo da recusa',
          description: `Digite o motivo da recusa para ${selectedCount === 1 ? 'o item selecionado' : `os ${selectedCount} itens selecionados`}.`,
          labelConfirm: 'Desejo recusar e enviar motivo da recusa',
          labelCancel: 'Recusar sem motivos'
        },
        {
          justificationPlaceholder: 'Digite aqui o motivo pelo qual os itens foram recusados.',
          // "Recusar e enviar motivo" só libera com motivo escrito; sem motivo, o caminho é "Recusar sem motivos".
          justificationRequired: true
        },
        ModalTypes.DANGER
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (result.action === Action.CLOSED) {
          return;
        }

        if (result.action === Action.CANCEL) {
          this.confirmBatchDeclineWithoutReason(selectedCount).subscribe(confirmed => {
            if (confirmed) {
              const selectedIds = this.selectedItemIds();
              this.itemStatuses.update(statuses => {
                const next = { ...statuses };
                selectedIds.forEach(id => {
                  next[id] = PurchaseItemStatus.DECLINED;
                });
                return next;
              });
              this.declineReasons.update(reasons => {
                const next = { ...reasons };
                selectedIds.forEach(id => {
                  next[id] = this._defaultReason;
                });
                return next;
              });
              this.clearSelection();
              this.snackBar.warning(
                `${selectedCount} ${itemNoun} ${declinedNoun}. Não esqueça de confirmar o envio.`
              );
            }
          });
          return;
        }

        const selectedIds = this.selectedItemIds();
        const reason = result.justification || this._defaultReason;
        this.itemStatuses.update(statuses => {
          const next = { ...statuses };
          selectedIds.forEach(id => {
            next[id] = PurchaseItemStatus.DECLINED;
          });
          return next;
        });
        this.declineReasons.update(reasons => {
          const next = { ...reasons };
          selectedIds.forEach(id => {
            next[id] = reason;
          });
          return next;
        });
        this.clearSelection();
        this.snackBar.warning(
          `${selectedCount} ${itemNoun} ${declinedNoun}. Não esqueça de confirmar o envio.`
        );
      });
  }

  private confirmBatchDeclineWithoutReason(count: number) {
    return this.confirmationModal
      .open(
        {
          headerTitle: 'Recusar itens',
          title: 'Deseja mesmo recusar esses itens?',
          description: `${count === 1 ? 'O item selecionado será marcado' : `Os ${count} itens selecionados serão marcados`} como recusados sem justificativa.`,
          labelConfirm: 'Desejo recusar',
          labelCancel: 'Cancelar'
        },
        ModalTypes.DANGER
      )
      .pipe(map((action: Action) => action === Action.CONFIRM));
  }

  protected handleGoBack(): void {
    if (this.returnUrl) {
      this.router.navigateByUrl(this.returnUrl);
      return;
    }

    this.router.navigate(['../../quadro'], { relativeTo: this.route });
  }

  protected handleTabChange(event: MatTabChangeEvent): void {
    const tab = event.index === PurchaseRequestDetailTab.ATTACHMENTS ? 'anexos' : 'detalhes';
    this.selectedTabIndex.set(event.index);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  protected handleTableAction(event: PurchaseRequestItemTableActionEvent): void {
    const item = event.item;
    switch (event.actionType) {
      case 'approve-item':
        this.approveItem(item);
        break;
      case 'decline-item':
        this.declineItem(item);
        break;
      case 'change-reason':
        this.changeDeclineReason(item);
        break;
    }
  }

  private approveItem(item: SendPurchaseRequestForQuoteItem): void {
    this.itemStatuses.update(statuses => ({
      ...statuses,
      [item.orderId]: PurchaseItemStatus.APPROVED
    }));
    this.declineReasons.update(reasons => {
      const { [item.orderId]: removed, ...rest } = reasons;
      return rest;
    });
    // Mesmo lembrete das ações em lote: nada é salvo até "Confirmar".
    this.snackBar.success('1 item aprovado. Não esqueça de confirmar o envio.');
  }

  private declineItem(item: SendPurchaseRequestForQuoteItem): void {
    this.confirmationModal
      .openWithJustification(
        {
          headerTitle: 'Recusar item',
          title: 'Motivo da recusa',
          description: `Digite o motivo da recusa para o produto ${item.name}.`,
          labelConfirm: 'Desejo recusar e enviar motivo da recusa',
          labelCancel: 'Recusar sem motivos'
        },
        {
          justificationPlaceholder: 'Digite aqui o motivo pelo qual o item foi recusado.',
          // "Recusar e enviar motivo" só libera com motivo escrito; sem motivo, o caminho é "Recusar sem motivos".
          justificationRequired: true
        },
        ModalTypes.DANGER
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: ConfirmResult) => {
        if (result.action === Action.CLOSED) {
          return;
        }

        if (result.action === Action.CANCEL) {
          this.confirmDeclineWithoutReason(item).subscribe(confirmed => {
            if (confirmed) {
              this.itemStatuses.update(statuses => ({
                ...statuses,
                [item.orderId]: PurchaseItemStatus.DECLINED
              }));
              this.snackBar.warning('1 item recusado. Não esqueça de confirmar o envio.');
              this.declineReasons.update(reasons => ({
                ...reasons,
                [item.orderId]: this._defaultReason
              }));
            }
          });
          return;
        }

        this.itemStatuses.update(statuses => ({
          ...statuses,
          [item.orderId]: PurchaseItemStatus.DECLINED
        }));
        this.snackBar.warning('1 item recusado. Não esqueça de confirmar o envio.');
        this.declineReasons.update(reasons => ({
          ...reasons,
          [item.orderId]: result.justification || this._defaultReason
        }));
      });
  }

  private confirmDeclineWithoutReason(item: SendPurchaseRequestForQuoteItem) {
    return this.confirmationModal
      .open(
        {
          headerTitle: 'Recusar item',
          title: 'Deseja mesmo recusar esse item?',
          description: `O item ${item.name} será marcado como recusado sem justificativa.`,
          labelConfirm: 'Desejo recusar',
          labelCancel: 'Cancelar'
        },
        ModalTypes.DANGER
      )
      .pipe(map((action: Action) => action === Action.CONFIRM));
  }

  private changeDeclineReason(item: SendPurchaseRequestForQuoteItem): void {
    const currentReason = this.declineReasons()[item.orderId] || '';

    this.confirmationModal
      .openWithJustification(
        {
          headerTitle: 'Alterar motivo da recusa',
          title: 'Atualize o motivo da recusa',
          description: `Altere o motivo da recusa para o item ${item.name}.`,
          labelConfirm: 'Salvar motivo',
          labelCancel: 'Cancelar'
        },
        {
          justificationPlaceholder: 'Digite o novo motivo da recusa',
          justificationRequired: true
        },
        ModalTypes.DANGER
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: ConfirmResult) => {
        if (result.action !== Action.CONFIRM) {
          return;
        }

        this.declineReasons.update(reasons => ({
          ...reasons,
          [item.orderId]: result.justification || currentReason || this._defaultReason
        }));
      });
  }

  protected handleQuantityChange(event: { item: SendPurchaseRequestForQuoteItem; newQuantity: number }): void {
    const { item, newQuantity } = event;

    this.loadingService.showLoading();
    this.purchasingService
      .updatePurchaseItemInfo(item.orderId, { quantity: newQuantity })
      .pipe(
        finalize(() => this.loadingService.hideLoading()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.items.update(currentItems =>
            currentItems.map(i =>
              i.orderId === item.orderId ? { ...i, quantity: newQuantity } : i
            )
          );
          this.snackBar.success('Quantidade alterada com sucesso!');
        },
        error: () => {
          this.snackBar.error('Erro ao alterar a quantidade do item.');
        }
      });
  }

  protected handleConfirm(): void {
    this.confirmationModal
      .open(
        {
          headerTitle: 'Confirmar solicitação',
          title: `Confirmar a revisão da solicitação #${this.currentPurchaseId}?`,
          description: this.confirmSummary(),
          labelConfirm: 'Confirmar e publicar',
          labelCancel: 'Ainda não'
        },
        ModalTypes.INFO
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (result === Action.CONFIRM) {
          this.submitConfirmation();
        }
      });
  }

  /** Resumo do que a confirmação faz, com as contagens da revisão. */
  private confirmSummary(): string {
    const approved = this.approvedCount();
    const declined = this.declinedCount();
    const itens = (n: number) => (n === 1 ? '1 item' : `${n} itens`);
    const lines: string[] = [];

    if (approved > 0) {
      lines.push(
        `**${itens(approved)} ${approved === 1 ? 'aprovado segue' : 'aprovados seguem'}** para a próxima etapa (Aceitas).`
      );
    }
    if (declined > 0) {
      lines.push(
        `**${itens(declined)} ${declined === 1 ? 'recusado fica' : 'recusados ficam'}** fora da compra, com o motivo informado.`
      );
    }
    if (approved === 0) {
      lines.push('Nenhum item foi aprovado: a solicitação não segue para a próxima etapa.');
    }
    return lines.join('\n');
  }

  private submitConfirmation(): void {
    const currentStatuses = this.itemStatuses();
    const currentReasons = this.declineReasons();

    const payload: UpdatePurchaseOrderItemDto[] = this.items().map(item => {
      const status = currentStatuses[item.orderId] || PurchaseItemStatus.APPROVED;
      return {
        orderId: item.orderId,
        status,
        reason: status === PurchaseItemStatus.DECLINED ? currentReasons[item.orderId] || this._defaultReason : undefined
      };
    });

    this.loadingService.showLoading();
    this.purchasingService
      .updatePurchaseOrdersStatus(this.currentPurchaseId, payload)
      .pipe(
        finalize(() => this.loadingService.hideLoading()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.snackBar.success('Solicitação confirmada com sucesso!');
          this.leavingAfterConfirm.set(true);
          this.handleGoBack();
        },
        error: () => {
          this.snackBar.error('Erro ao confirmar a solicitação.');
        }
      });
  }

  private loadDetail(purchaseId: string): void {
    const resolvedPurchase = this.route.snapshot.data[
      'purchase'
    ] as PurchaseRequestResponseDto | null;

    if (!purchaseId || !resolvedPurchase) {
      this.snackBar.error('Solicitação não encontrada.');
      this.handleGoBack();
      return;
    }

    this.purchase.set({ ...resolvedPurchase, status: [PurchaseRequestStatus.PENDING] });
    this.detailsLoadState.set(LoadState.LOADING);
    this.purchasingService
      .findAllProductsForQuote(purchaseId, PurchaseRequestStatus.PENDING)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.items.set(response.body || []);
          this.detailsLoadState.set(LoadState.LOADED);
        },
        error: () => {
          this.detailsLoadState.set(LoadState.ERROR);
          this.showErrorDialog(() => this.loadDetail(purchaseId));
        }
      });
  }

  private showErrorDialog(reloadAction: () => void): void {
    this.confirmationModal
      .open(
        {
          headerTitle: 'Erro ao carregar',
          title: 'Não foi possível carregar os dados',
          description:
            'Ocorreu um problema ao tentar buscar as informações. Deseja tentar novamente?',
          labelConfirm: 'Recarregar',
          labelCancel: 'Entendi'
        },
        ModalTypes.DANGER
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => {
        if (res === Action.CONFIRM) {
          reloadAction();
        } else {
          this.handleGoBack();
        }
      });
  }

  protected openActivityTab(): void {
    this.activityPanel().openTab(0);
  }

  protected openCommentsTab(): void {
    this.activityPanel().openTab(1);
  }
}

